import { CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaStar } from "react-icons/fa";

const getInflatedTotal = (productId) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return 50000;
  }

  const STORAGE_KEY = `product_rating_count_${productId}`;
  const MIN_FAKE_TOTAL = 32000;
  const MAX_FAKE_TOTAL = 99999;
  const range = MAX_FAKE_TOTAL - MIN_FAKE_TOTAL;

  const storedCount = localStorage.getItem(STORAGE_KEY);

  if (storedCount) {
    return parseInt(storedCount, 10);
  }

  const newCount = Math.floor(Math.random() * range) + MIN_FAKE_TOTAL;

  localStorage.setItem(STORAGE_KEY, newCount);

  return newCount;
};

const calculateRatingData = (productReviews, productId) => {
  const starCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  let totalRatingSum = 0;
  const actualTotalReviews = productReviews.length;

  const inflatedTotalReviews = getInflatedTotal(productId);

  let averageRating = 4.1;
  let calculatedAverage = 4.1;

  if (actualTotalReviews > 0) {
    productReviews.forEach((review) => {
      const rating = Math.round(review.rating);
      if (rating >= 1 && rating <= 5) {
        starCounts[rating] += 1;
        totalRatingSum += rating;
      }
    });
    calculatedAverage = totalRatingSum / actualTotalReviews;
  }

  const idString = String(productId);
  let idSeed = 0;
  for (let i = 0; i < idString.length; i++) {
    idSeed = (idSeed * 31 + idString.charCodeAt(i)) % 10;
  }

  if (calculatedAverage >= 4.5) {
    averageRating = 4.5 + Math.min(idSeed, 4) * 0.1;
  } else {
    averageRating = calculatedAverage;
  }

  averageRating = parseFloat(averageRating.toFixed(1));

  let finalStarCounts = {};
  const ratingKeys = [5, 4, 3, 2, 1];
  let distributionProportions = {};

  if (actualTotalReviews === 0) {
    distributionProportions = { 5: 0.55, 4: 0.25, 3: 0.1, 2: 0.06, 1: 0.04 };
  } else {
    let tempProportions = {};
    let totalProp = 0;
    ratingKeys.forEach((star) => {
      let proportion = starCounts[star] / actualTotalReviews;
      const desiredWeight = (star / 5) * 0.2 + 0.1;
      tempProportions[star] = proportion * 0.7 + desiredWeight * 0.3;
      totalProp += tempProportions[star];
    });

    ratingKeys.forEach((star) => {
      distributionProportions[star] = tempProportions[star] / totalProp;
    });
  }

  let sumOfFakeCounts = 0;
  ratingKeys.forEach((star) => {
    finalStarCounts[star] = Math.round(
      distributionProportions[star] * inflatedTotalReviews
    );
    sumOfFakeCounts += finalStarCounts[star];
  });

  finalStarCounts[5] += inflatedTotalReviews - sumOfFakeCounts;

  return {
    averageRating: averageRating,
    totalRatings: inflatedTotalReviews,
    totalReviews: inflatedTotalReviews,
    starCounts: finalStarCounts,
  };
};
const RatingBar = ({ star, count, percentage, barColor }) => {
  return (
    <div style={ratingChartStyles.ratingBarRow}>
      <div style={ratingChartStyles.starLabel}>
        {star}
        <FaStar style={ratingChartStyles.smallStarIcon} />
      </div>
      <div style={ratingChartStyles.barContainer}>
        <div
          style={{
            ...ratingChartStyles.ratingBarFill,
            width: `${percentage}%`,
            backgroundColor: barColor,
          }}
        ></div>
      </div>
      <div style={ratingChartStyles.ratingCount}>{count.toLocaleString()}</div>
    </div>
  );
};

const DynamicStarRatingChart = ({ reviews, productId }) => {
  const reviewData = calculateRatingData(reviews || [], productId);
  const { averageRating, totalReviews, starCounts } = reviewData;

  if (totalReviews === 0) {
    return (
      <div style={ratingChartStyles.noReviews}>
        No ratings and reviews available yet.
      </div>
    );
  }

  const maxCount = starCounts[5] || totalReviews;
  const ratingKeys = [5, 4, 3, 2, 1];

  return (
    <div
      className="product-data5"
      style={ratingChartStyles.ratingChartContainer}
    >
      <div style={ratingChartStyles.ratingSummary}>
        <div style={ratingChartStyles.averageRatingSection}>
          <span style={ratingChartStyles.averageRatingNumber}>
            {averageRating.toFixed(1)}
          </span>
          <FaStar style={ratingChartStyles.averageStarIcon} />
        </div>
        <div style={ratingChartStyles.reviewCounts}>
          <div style={ratingChartStyles.totalRatingsCount}>
            {totalReviews.toLocaleString()}
          </div>
          <div style={ratingChartStyles.ratingsText}>Ratings & Reviews</div>
        </div>
      </div>

      <div style={ratingChartStyles.ratingBars}>
        {ratingKeys.map((star) => {
          const count = starCounts[star];

          const percentage = (count / maxCount) * 100;

          let barColor = "#4caf50";
          if (star === 3) {
            barColor = "#ffc107";
          } else if (star === 2) {
            barColor = "#ff9800";
          } else if (star === 1) {
            barColor = "#f44336";
          }

          return (
            <RatingBar
              key={star}
              star={star}
              count={count}
              percentage={star === 5 ? 100 : percentage}
              barColor={barColor}
            />
          );
        })}
      </div>
    </div>
  );
};

const ratingChartStyles = {
  ratingChartContainer: {
    display: "flex",
    padding: "20px",
    maxWidth: "100%",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#fff",
    fontFamily: "Arial, sans-serif",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    margin: "10px 6px",
  },
  noReviews: {
    padding: "20px",
    maxWidth: "100%",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#fff",
    textAlign: "center",
    fontFamily: "Arial, sans-serif",
    color: "#5f6368",
    margin: "10px 0",
  },

  ratingSummary: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginRight: "20px",
    paddingRight: "20px",
    borderRight: "1px solid #eee",
    minWidth: "120px",
  },
  averageRatingSection: {
    display: "flex",
    alignItems: "center",
    marginBottom: "5px",
  },
  averageRatingNumber: {
    fontSize: "25px",
    fontWeight: "500",
    lineHeight: "1",
    color: "#212121",
  },
  averageStarIcon: {
    fontSize: "14px",
    color: "#212121",
    marginLeft: "5px",
  },
  reviewCounts: {
    textAlign: "center",
    lineHeight: "1.2",
    marginTop: "5px",
  },
  totalRatingsCount: {
    fontSize: "1.1em",
    fontWeight: "bold",
    color: "#5f6368",
  },
  ratingsText: {
    fontSize: "0.8em",
    color: "#5f6368",
  },

  ratingBars: {
    flexGrow: 1,
    width: "100%",
  },
  ratingBarRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "4px",
  },
  starLabel: {
    fontSize: "0.9em",
    color: "#212121",
    width: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginRight: "5px",
    fontWeight: "600",
  },
  smallStarIcon: {
    fontSize: "0.7em",
    color: "#212121",
    marginLeft: "2px",
  },
  barContainer: {
    flexGrow: 1,
    height: "6px",
    backgroundColor: "#f1f1f1",
    borderRadius: "4px",
    margin: "0 8px",
    overflow: "hidden",
  },
  ratingBarFill: {
    height: "100%",
    borderRadius: "4px",
    transition: "width 0.5s ease-out",
  },
  ratingCount: {
    fontSize: "0.8em",
    color: "#5f6368",
    minWidth: "40px",
    textAlign: "right",
  },
};

function ProductDetail() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { id } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const INITIAL_TIME = 12 * 60 + 43;
  const [timeLeft, setTimeLeft] = useState(INITIAL_TIME);
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [isBot, setIsBot] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const botKeywords = [
      "bot",
      "crawl",
      "spider",
      "slurp",
      "fetch",
      "headless",
      "curl",
      "python",
      "axios",
      "postman"
    ];
    const detectedBot = botKeywords.some((word) => ua.includes(word));
    setIsBot(detectedBot);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev <= 1 ? INITIAL_TIME : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [INITIAL_TIME]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}min ${secs.toString().padStart(2, "0")}sec`;
  };

  useEffect(() => {
    fetch("/product-data.json")
      .then((res) => res.json())
      .then((data) => {
        setProducts(data);
        const foundProduct = data.find((p) => String(p.id) === String(id));
        setProduct(foundProduct || null);
      })
      .catch((err) => console.error("Error loading product:", err));
  }, [id]);

  useEffect(() => {
    if (!product || !product.slide || product.slide.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % product.slide.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [product]);

  const goToSlide = (index) => setCurrentIndex(index);

  const addToCart = (product) => {
    const existingIndex = cart.findIndex((item) => item.id === product.id);
    let newCart = [...cart];
    if (existingIndex !== -1) {
      newCart[existingIndex].quantity += 1;
    } else {
      newCart.push({ ...product, quantity: 1 });
    }
    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
    navigate("/cart");
  };

  if (!product) {
    return (
      <main className="container">
        <div style={{ position: "relative" }}>
          <h2
            style={{
              color: "red",
              textAlign: "center",
              padding: "100px",
            }}
          >
            <CircularProgress />
          </h2>
        </div>
      </main>
    );
  }

  return (
    <>
      <header id="header">
        <div className="container">
          <div className="main-header">
            <div
              className="main-bar"
              style={{
                backgroundColor: isBot ? "green" : "#2874f0",
                alignItems: "center",
              }}
            >
              <div
                className="main-menu"
                style={{ display: "flex", alignItems: "center" }}
              >
                <Link to="/" className="menu-toggle">
                  <i className="fa-solid fa-arrow-left"></i>
                </Link>

                {isBot ? (
                  <h2 style={{ color: "white", fontSize: "15px" }}>
                    ShopSmart
                  </h2>
                ) : (
                  <a href="/Home" className="logo">
                    <img src="/img/logo.png" alt="Logo" />
                  </a>
                )}
              </div>

              <div className="main-cart">
                <button
                  style={{ background: "none", border: "none", color: "white" }}
                  onClick={() => navigate("/cart")}
                >
                  <i className="fa-solid fa-cart-shopping"></i>
                  {cart.length > 0 && (
                    <span className="header-cart">{cart.length}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="banner-sec">
          <div className="container">
            {product.slide && product.slide.length > 0 && (
              <div className="slider">
                <div
                  className="slides"
                  style={{
                    transform: `translateX(-${currentIndex * 100}%)`,
                    display: "flex",
                    transition: "transform 0.5s ease-in-out",
                  }}
                >
                  {product.slide.map((img, index) => (
                    <div className="slide" key={index}>
                      <img src={img} alt={`slide-${index}`} />
                    </div>
                  ))}
                </div>
                <div className="dots">
                  {product.slide.map((_, index) => (
                    <div
                      key={index}
                      className={`dot ${currentIndex === index ? "active" : ""}`}
                      onClick={() => goToSlide(index)}
                    ></div>
                  ))}
                </div>
              </div>
            )}

            <div className="product-data1">
              <div className="product-title">{product.name}</div>
              {product.trending && (
                <div
                  style={{
                    backgroundColor: "#ff9100ff",
                    color: "#fff",
                    padding: "3px 6px",
                    fontSize: "10px",
                    fontWeight: "600",
                    borderBottomRightRadius: "6px",
                    borderTopLeftRadius: "6px",
                    width: "55px",
                  }}
                >
                  Trending
                </div>
              )}

              <img
                className="detail-promo-logo"
                src="/img/pry-3.png"
                alt="Offer"
              />

              {isBot ? (
                <div className="p-pricedet">
                  <span className="mrp">{product.total}</span>
                </div>
              ) : (
                <div className="detail-price-block">
                  <div className="detail-discount-line">
                    <span className="discount-arrow">↓</span>
                    <span className="dis">{product.dis} Off</span>
                    <span className="mrp">{product.total}</span>
                  </div>
                  <div className="detail-sale-line">
                    <strong>{product.disprice}</strong>
                    <img src="/img/wow.png" alt="Special offer" />
                  </div>
                </div>
              )}

              {product.discription && (
                <h3 className="detail-description">{product.discription}</h3>
              )}
            </div>

            <div className="product-data2">
              <h4 className="offer-time">
                Offer ends in <span>{formatTime(timeLeft)}</span>
              </h4>
            </div>

            {!isBot && (
              <>
                <div className="product-data3">
                  <img src="/img/pro.png" alt="Offer benefits" />
                </div>
                <div className="product-data4">
                  <div className="pry-1">
                    <img src="/img/pry-1.png" alt="7-day replacement" />
                    <h5>7 days Replacement</h5>
                  </div>
                  <div className="pry-1">
                    <img src="/img/pry-2.png" alt="No cash on delivery" />
                    <h5>No Cash On Delivery</h5>
                  </div>
                  <div className="pry-3">
                    <img src="/img/pry-3.png" alt="Assured badge" />
                    <h5 style={{ paddingTop: "10px" }}>Plus(F-Assured)</h5>
                  </div>
                </div>
              </>
            )}

            <div className="detail-before-rating-gallery">
              {(product.slide || [product.mainimg]).map((img, index) => (
                <img
                  key={`${img}-${index}`}
                  src={img}
                  alt={`${product.name} view ${index + 1}`}
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = "/img/p-1.jpg";
                  }}
                />
              ))}
            </div>

            <div className="container">
              {product && product.id && (
                <DynamicStarRatingChart
                  reviews={product.reviews}
                  productId={product.id}
                />
              )}
            </div>

            <section>
              <div className="container">
                {product.reviews && product.reviews.length > 0 ? (
                  product.reviews.map((rev, index) => (
                    <div key={index} className="review-card">
                      <div className="rating">
                        <div className="star-box">{rev.rating} ★</div>
                        <span style={{ fontWeight: "600" }}>{rev.title}</span>
                      </div>
                      <p className="review-text">{rev.text}</p>
                      <div className="buyer-info">
                        <div>
                          <span className="buyer-name">{rev.buyerName}</span>
                          {rev.verified && (
                            <span className="verified">✔ Certified Buyer,</span>
                          )}
                          <span className="location">{rev.location}</span>
                        </div>
                        <span>{rev.daysAgo}</span>
                      </div>
                      {rev.images && rev.images.length > 0 && (
                        <div className="image-row">
                          {rev.images.map((img, i) => (
                            <img
                              key={i}
                              src={img}
                              alt={`Review ${index + 1} ${i + 1}`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p style={{ padding: "10px", color: "#888" }}>
                    No reviews yet.
                  </p>
                )}
              </div>
            </section>

            <section className="cat-sec">
              <div className="container">
                <div className="pro-data">
                  <h3
                    style={{
                      fontSize: "16px",
                      color: "#474747ff",
                      marginLeft: "8px",
                    }}
                  >
                    Similar Products:
                  </h3>
                  <div className="cat-link">
                    {products
                      .filter((p) => String(p.id) !== String(product.id)) // Selected product ko nikal dega
                      .map((p, index) => (
                        <div
                          key={`${p.id}-${index}`}
                          className="product"
                          style={{ textDecoration: "none", cursor: "pointer" }}
                          onClick={() => (window.location.href = `/ProductDetail/${p.id}`)}
                        >
                          <div className="product-img">
                            <div className="img-det">
                              <div className="img-detail">
                                {/* ✅ Yahan 'product' ki jagah 'p' use karein */}
                                <img src={p.mainimg} alt={p.name} />
                              </div>
                            </div>
                          </div>

                          <div className="product-info" style={{ color: "black" }}>
                            {/* ✅ Yahan bhi 'p' use karein */}
                            {p.name}
                          </div>

                          {!isBot ? (
                            <>
                              <div
                                className="price-det"
                                style={{
                                  fontSize: "17px",
                                  fontWeight: "600",
                                  color: "#080808ff",
                                  marginTop: "8px",
                                  marginBottom: "8px"
                                }}
                              >
                                <div className="discount-line">
                                  <i className="fa-solid fa-arrow-down"></i>
                                  <b>{p.dis} Off</b>
                                  <span>{p.total}</span>
                                </div>
                                <div className="sale-line">
                                  <b>{p.disprice}</b>
                                  <img src="/img/wow.png" alt="Offer badge" />
                                </div>
                              </div>
                            </>
                          ) : (
                            <div
                              className="price-det"
                              style={{
                                fontSize: "14px",
                                color: "green",
                                marginTop: "8px",
                                marginBottom: "8px",
                                fontWeight: "600",
                              }}
                            >
                              {p.total}
                            </div>
                          )}

                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div className="delivery-row">
                              <i className="fa-solid fa-truck-fast express-icon"></i>
                              <span className="express-text">EXPRESS</span>
                              <p className="delivery-date">
                                Delivery {p.delivery_day || 'Tomorrow'}
                              </p>
                            </div>
                            <span className="assured-badge">
                              <img src="/img/salec.png" style={{ width: "60px" }} alt="sale" />
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>

                </div>
              </div>
            </section>
          </div>
        </section>
      </main>
      <footer id="footer">
        <div className="container">
          <div className="footer-btn">
            <button
              className="b-btn add-to-cart-btn"
              onClick={() => addToCart(product)}
            >
              Add to cart
            </button>
            <button
              className="b-btn buy-now-btn"
              onClick={() => addToCart(product)}
            >
              Buy at {product.disprice}
            </button>
          </div>
        </div>
      </footer>
    </>
  );
}

export default ProductDetail;